import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Scale, Heart, Users } from 'lucide-react';
import { attorney } from '../mock';

const Firm = () => {
  return (
    <div style={{ marginTop: '70px' }}>
      {/* Hero Section */}
      <section style={{
        background: 'var(--bg-card)',
        padding: '80px 0',
        textAlign: 'center'
      }}>
        <div className="container">
          <h1 className="heading-1" style={{ color: 'var(--brand-primary)', marginBottom: '24px' }}>
            About LG Law Office
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto' }}>
            A trusted name in personal injury law, dedicated to fighting for justice and maximum compensation for our clients
          </p>
        </div>
      </section>

      {/* Mission Statement */}
      <section style={{ padding: '96px 0', background: 'var(--bg-page)' }}>
        <div className="container">
          <div style={{
            maxWidth: '1000px',
            margin: '0 auto',
            textAlign: 'center'
          }}>
            <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '32px' }}>
              Our Mission
            </h2>
            <p className="body-large" style={{ color: 'var(--text-white)', lineHeight: '1.8', marginBottom: '48px' }}>
              At LG Law Office, our mission is simple: to provide exceptional legal representation to injury victims throughout California. We believe that everyone deserves access to quality legal counsel, regardless of their financial situation. That's why we work on a contingency fee basis – you don't pay unless we win your case.
            </p>
            <p className="body-large" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
              We understand that suffering an injury can be one of the most challenging times in your life. Medical bills pile up, you may be unable to work, and insurance companies try to minimize your claim. Our experienced legal team fights aggressively to ensure you receive the compensation you deserve, allowing you to focus on recovery while we handle the legal complexities.
            </p>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section style={{ padding: '96px 0', background: 'var(--bg-card)' }}>
        <div className="container">
          <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '64px', textAlign: 'center' }}>
            Our Core Values
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '48px'
          }}>
            {[
              {
                icon: Scale,
                title: 'Justice',
                description: 'We believe in fighting for what\'s right and holding negligent parties accountable for their actions.'
              },
              {
                icon: Heart,
                title: 'Compassion',
                description: 'We treat every client with empathy and understanding, recognizing the personal toll of injuries.'
              },
              {
                icon: Award,
                title: 'Excellence',
                description: 'We maintain the highest standards of legal representation and strive for outstanding results.'
              },
              {
                icon: Users,
                title: 'Commitment',
                description: 'We\'re dedicated to our clients\' success and provide personalized attention to every case.'
              }
            ].map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div
                  key={index}
                  style={{
                    textAlign: 'center',
                    padding: '32px',
                    animation: `fadeIn 0.6s ease-out ${index * 0.1}s backwards`
                  }}
                >
                  <div style={{
                    width: '80px',
                    height: '80px',
                    background: 'var(--brand-primary)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 24px'
                  }}>
                    <IconComponent size={40} color="var(--text-inverse)" />
                  </div>
                  <h3 className="heading-4" style={{ color: 'var(--text-white)', marginBottom: '16px' }}>
                    {value.title}
                  </h3>
                  <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    {value.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Attorney Profile */}
      <section style={{ padding: '96px 0', background: 'var(--bg-page)' }}>
        <div className="container">
          <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '64px', textAlign: 'center' }}>
            Meet Our Attorney
          </h2>
          <div className="responsive-grid-contact" style={{
            alignItems: 'center',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {/* Attorney Photo */}
            <div style={{
              position: 'relative',
              borderRadius: '16px',
              overflow: 'hidden',
              border: '3px solid var(--brand-primary)'
            }}>
              <img
                src={attorney.image}
                alt={attorney.fullName}
                style={{
                  width: '100%',
                  height: '500px',
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
            </div>

            {/* Attorney Info */}
            <div>
              <h3 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '8px' }}>
                {attorney.name}
              </h3>
              <p style={{ color: 'var(--text-secondary)', fontSize: '1.25rem', marginBottom: '32px' }}>
                {attorney.title}
              </p>
              <p className="body-medium" style={{ color: 'var(--text-white)', lineHeight: '1.8', marginBottom: '32px' }}>
                {attorney.bio}
              </p>
              
              {/* Credentials */}
              <div style={{
                background: 'var(--bg-card)',
                padding: '32px',
                borderRadius: '12px',
                border: '1px solid var(--border-light)'
              }}>
                <h4 className="heading-5" style={{ color: 'var(--brand-primary)', marginBottom: '20px' }}>
                  Credentials & Memberships
                </h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {attorney.credentials.map((credential, index) => (
                    <li
                      key={index}
                      style={{
                        color: 'var(--text-white)',
                        padding: '12px 0',
                        borderBottom: index < attorney.credentials.length - 1 ? '1px solid var(--border-light)' : 'none',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px'
                      }}
                    >
                      <span style={{ color: 'var(--brand-primary)', fontSize: '1.2rem' }}>•</span>
                      <span>{credential}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Link
                to="/free-consultation"
                className="btn-primary"
                style={{ marginTop: '32px', display: 'inline-flex' }}
              >
                Schedule a Consultation
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section style={{ padding: '96px 0', background: 'var(--bg-card)' }}>
        <div className="container">
          <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
            <h2 className="heading-2" style={{ color: 'var(--brand-primary)', marginBottom: '48px', textAlign: 'center' }}>
              Our Approach
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
              {[
                {
                  step: '01',
                  title: 'Free Consultation',
                  description: 'We start with a free, no-obligation consultation to understand your case and explain your legal options.'
                },
                {
                  step: '02',
                  title: 'Investigation',
                  description: 'Our team thoroughly investigates your case, gathering evidence, interviewing witnesses, and consulting with experts.'
                },
                {
                  step: '03',
                  title: 'Negotiation',
                  description: 'We aggressively negotiate with insurance companies to secure the maximum settlement for your injuries.'
                },
                {
                  step: '04',
                  title: 'Litigation',
                  description: 'If necessary, we\'re prepared to take your case to trial and fight for your rights in court.'
                }
              ].map((item, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    gap: '32px',
                    alignItems: 'flex-start',
                    padding: '32px',
                    background: 'var(--bg-page)',
                    borderRadius: '12px',
                    border: '1px solid var(--border-light)',
                    animation: `fadeIn 0.6s ease-out ${index * 0.1}s backwards`
                  }}
                >
                  <div style={{
                    fontSize: '3rem',
                    fontWeight: '900',
                    color: 'var(--brand-primary)',
                    lineHeight: '1',
                    minWidth: '80px'
                  }}>
                    {item.step}
                  </div>
                  <div>
                    <h3 className="heading-4" style={{ color: 'var(--text-white)', marginBottom: '12px' }}>
                      {item.title}
                    </h3>
                    <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Firm;